<template>
  <div class="tnc-body">
    <h5>{{sellerName}} Pass Terms and Conditions</h5>
    <p>
      These terms apply when you buy a Pass from <b>{{sellerName}}</b> through Show Me The Menu.
      By paying, you agree to them.
    </p>

    <h6>1. The seller</h6>
    <p>
      The Pass is sold by <b>{{sellerLegalName}}</b>{{tradingAs}} (the “Seller”).
      The contract for the Pass is between you and the Seller, not Show Me The Menu.
    </p>
    <p v-if="addressText">
      {{addressText}}<span v-if="merchant.phoneNumber"><br/>Tel: {{merchant.phoneNumber}}</span>
    </p>
    <p>
      Show Me The Menu is a brand of Borium Studio Limited. It provides the website, payment page,
      pass QR, email and wallet buttons only. Card payments are taken by Stripe for the Seller.
    </p>

    <h6>2. What you are buying</h6>
    <p>
      You are buying a prepaid Pass for this Seller only. It is not cash, not a gift card, and not
      valid at any other business.
    </p>
    <p v-if="plan">
      This purchase: <b>{{plan.title}}</b> for £{{priceText}}.
      {{plan.usesPerWeek || 5}} uses per week (Monday to Sunday).
      Each use covers {{plan.foodPerUse || 1}} food item and {{plan.drinkPerUse || 1}} drink item,
      as set by the Seller.
      <span v-if="plan.validDays > 0">The Pass is valid for {{plan.validDays}} days from the start date.</span>
      <span v-else>The Pass has no expiry date, unless the Seller voids it.</span>
    </p>
    <p v-else>
      Each Pass product shows its price, weekly uses, food and drink per use, and whether it expires.
      Unused uses do not carry over to the next week.
    </p>

    <h6>3. Start date</h6>
    <p>
      You must choose a start date before you pay. It cannot be earlier than today.
      You cannot redeem the Pass before that date.
      If the Pass has a validity period, it is counted from the start date.
    </p>

    <h6>4. How you redeem</h6>
    <p>
      Show the Pass QR code (or wallet pass) at the Seller’s till or kiosk.
      On each use the Pass covers the highest-priced eligible food and drink in the basket,
      up to the allowance for that product.
    </p>
    <ul>
      <li>Alcohol is never covered.</li>
      <li>Only items the Seller has made eligible are covered (price cap or linked menu items).</li>
      <li>If only food or only drink is eligible, that still counts as one use.</li>
      <li>If nothing eligible is in the basket, no use is taken.</li>
    </ul>
    <p>
      Extra items, extras above the cap, and anything not covered must be paid in the usual way.
    </p>

    <h6>5. Payment, email and your Pass</h6>
    <p>
      Email is required. After payment we email you the Pass details, QR code and wallet buttons,
      and register the Pass to that email. Keep the email and QR safe. Anyone who shows the QR
      may be able to redeem the Pass.
    </p>
    <p>
      Name and phone are optional. We use your details only to issue and support this Pass,
      unless you separately join the Seller’s loyalty or marketing.
    </p>

    <h6>6. Loyalty points</h6>
    <p>
      If you are on the Seller’s Show Me The Menu loyalty scheme, a redeemed visit may earn
      loyalty points on the menu value covered by the Pass, plus anything you still pay.
      Points are a loyalty benefit from the Seller, not extra cash value of the Pass.
    </p>

    <h6>7. Refunds and cancellation</h6>
    <p>
      The Pass is prepaid and is not redeemable for cash. The Seller does not offer a refund
      if you change your mind after payment, except where the law requires one.
    </p>
    <p>
      If you buy at a distance you may have a legal right to cancel within 14 days.
      That right ends if the start date is today (the Pass can be used at once) or if you
      have already redeemed a use. To ask about cancellation, contact the Seller.
    </p>
    <p>
      The Seller may void a Pass. Voiding cancels remaining uses. It is not a refund.
    </p>

    <h6>8. Problems</h6>
    <p>
      If you cannot redeem a valid Pass, or you believe a use was taken in error, contact the Seller.
      Show Me The Menu hosts the Pass record but does not decide menu eligibility, voiding, or refunds.
    </p>

    <h6>9. Changes</h6>
    <p>
      The Seller may update which menu items are eligible. The weekly allowance, food and drink
      per use, and price you paid stay as sold. These terms apply to this purchase.
    </p>
  </div>
</template>

<script>
export default {
  name: 'PassTnC',
  props: {
    merchant: {
      type: Object,
      default: function() {
        return {};
      }
    },
    plan: {
      type: Object,
      default: null
    }
  },
  computed: {
    sellerName: function() {
      return this.merchant && this.merchant.title ? this.merchant.title : 'the restaurant';
    },
    sellerLegalName: function() {
      if (this.merchant && this.merchant.registeredTitle) {
        return this.merchant.registeredTitle;
      }
      return this.sellerName;
    },
    tradingAs: function() {
      if (this.merchant && this.merchant.registeredTitle
          && this.merchant.title && this.merchant.registeredTitle !== this.merchant.title) {
        return ', trading as ' + this.merchant.title;
      }
      return '';
    },
    addressText: function() {
      if (!this.merchant) {
        return '';
      }
      let parts = [];
      if (this.merchant.address) parts.push(this.merchant.address);
      if (this.merchant.city) parts.push(this.merchant.city);
      if (this.merchant.postCode) parts.push(this.merchant.postCode);
      return parts.join(', ');
    },
    priceText: function() {
      if (!this.plan || !this.plan.salePrice) {
        return '0.00';
      }
      return (this.plan.salePrice / 100).toFixed(2);
    }
  }
}
</script>

<style scoped>
.tnc-body {
  text-align: left;
  padding: 10px 15px 20px 15px;
  font-size: 14px;
  color: #333;
  line-height: 1.45;
}
.tnc-body h5 {
  font-weight: 700;
  margin-bottom: 12px;
}
.tnc-body h6 {
  font-weight: 700;
  margin-top: 18px;
  margin-bottom: 6px;
}
.tnc-body ul {
  padding-left: 18px;
  margin-bottom: 10px;
}
</style>



// WEBPACK FOOTER //
// src/components/PassTnC.vue